import { create } from 'zustand'

export type NotificationLevel = 'positive' | 'negative' | 'info' | 'warning'

export interface NotificationAction {
  label: string
  onClick: () => void
}

export interface AppNotification {
  id: string
  message: string
  level: NotificationLevel
  /** Epoch milliseconds; stored as a number so it survives serialisation. */
  at: number
  read: boolean
  /**
   * Present only for the session that raised it. Actions are closures — they
   * cannot be written to storage — so a notification restored after a reload
   * keeps its text and loses its button.
   */
  action?: NotificationAction
}

const STORAGE_KEY = 'jlm:notifications'

/**
 * Enough to scroll through, few enough that the page stays readable and the
 * storage write stays small. Oldest fall off the end.
 */
const LIMIT = 50

function readStored(): AppNotification[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isNotification).slice(0, LIMIT)
  } catch {
    // Private-mode Safari, cleared storage, or a shape from an older release.
    return []
  }
}

function isNotification(value: unknown): value is AppNotification {
  if (typeof value !== 'object' || value === null) return false
  const n = value as Record<string, unknown>
  return (
    typeof n.id === 'string' &&
    typeof n.message === 'string' &&
    typeof n.at === 'number' &&
    typeof n.read === 'boolean' &&
    (n.level === 'positive' || n.level === 'negative' || n.level === 'info' || n.level === 'warning')
  )
}

function write(items: AppNotification[]): void {
  try {
    // Actions are dropped deliberately: a function cannot be serialised, and a
    // restored button would be a control that does nothing.
    const storable = items.map(({ action: _action, ...rest }) => rest)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storable))
  } catch {
    /* non-fatal: the log simply will not survive a reload */
  }
}

function newId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

interface NotificationState {
  items: AppNotification[]
  unread: number
  push: (input: { message: string; level?: NotificationLevel; action?: NotificationAction }) => void
  markAllRead: () => void
  remove: (id: string) => void
  clear: () => void
}

/**
 * The record of everything the app has told this user.
 *
 * These used to be toasts: shown for three and a half seconds, over the page,
 * then gone. That is the wrong medium for most of what goes through here — a
 * won key, an entry that was refused and why, an error worth quoting to
 * support. They are kept instead, and read at /my/notifications.
 */
export const useNotificationStore = create<NotificationState>((set, get) => ({
  items: readStored(),
  unread: readStored().filter((item) => !item.read).length,

  push: ({ message, level = 'info', action }) => {
    const item: AppNotification = {
      id: newId(),
      message,
      level,
      at: Date.now(),
      read: false,
      ...(action ? { action } : {}),
    }
    const items = [item, ...get().items].slice(0, LIMIT)
    write(items)
    set({ items, unread: items.filter((i) => !i.read).length })
  },

  markAllRead: () => {
    const items = get().items.map((item) => (item.read ? item : { ...item, read: true }))
    write(items)
    set({ items, unread: 0 })
  },

  remove: (id) => {
    const items = get().items.filter((item) => item.id !== id)
    write(items)
    set({ items, unread: items.filter((i) => !i.read).length })
  },

  clear: () => {
    write([])
    set({ items: [], unread: 0 })
  },
}))
